import { TimeFrameModel } from '../model'
import { ALL_DAYS, Day, Days } from '@e3dc-react/shell/Libs/date/types'

const ONE_DAY_IN_MINUTES = 24 * 60
const TOTAL_DAYS_IN_LEAP_YEAR = 366

/**
 * calculate minutes from midnight. '00:00' -> 0, '23:59' -> 1439
 */
const minutesFromMidnight = (time: Date): number => {
  return time.getHours() * 60 + time.getMinutes()
}

/**
 * convert date into day index (1 to 366)
 * Jan-01 -> 1, Jan-02 -> 2, .., Dec-30 -> 365, Dec-31 -> 366
 */
export const getDayOfYear = (date: Date): number => {
  const daysInMonth = [
    31, // January
    29, // February (1972 is a leap year)
    31, // March
    30, // April
    31, // May
    30, // June
    31, // July
    31, // August
    30, // September
    31, // October
    30, // November
    31, // December
  ]

  const month = date.getMonth()
  const day = date.getDate()

  /** convert date to index */
  let dayOfYear = 0
  for (let i = 0; i < month; i++) {
    dayOfYear += daysInMonth[i]
  }
  return dayOfYear + day
}

/**
 * Check if the date frames cover the whole year (leap year) without gaps or overlaps.
 * example: [jan-01 to april-30, may-1 to dec-31]
 */
export const isWholeYearCovered = (dateFrames: { from: Date; to: Date; timeFrames: TimeFrameModel[] }[]): boolean => {
  /* convert date to day index */
  const normalizedFrames = dateFrames.map(frame => {
    const from = new Date(frame.from)
    const to = new Date(frame.to)
    const dayIndexFrom = getDayOfYear(from)
    const dayIndexToo = getDayOfYear(to)

    return { from: dayIndexFrom, to: dayIndexToo }
  })

  /* Sort frames */
  normalizedFrames.sort((a, b) => a.from - b.from)

  /* check for gaps, overlaps and full coverage */
  let previousTo = 0
  let totalCoverage = 0

  for (let i = 0; i < normalizedFrames.length; i++) {
    const { from, to } = normalizedFrames[i]

    /* First frame must start on Jan-01 (day = 1) */
    if (i === 0 && from !== 1) {
      return false
    }

    /* check gap */
    if (from < previousTo + 1) {
      return false
    }
    /* check overlaps */
    if (from > previousTo + 1) {
      return false
    }

    totalCoverage += to - from + 1
    previousTo = to
  }

  /* Check if the entire year is covered */
  if (totalCoverage !== TOTAL_DAYS_IN_LEAP_YEAR) {
    return false
  }

  return true
}

/**
 * check if 'to' is after 'from' to nearest minutes from midnight
 */
export const isTimeAfter = (to: Date, from: Date): boolean => {
  const minutesFromMidnightInTo = minutesFromMidnight(to)
  const minutesFromMidnightInFrom = minutesFromMidnight(from)
  return minutesFromMidnightInTo > minutesFromMidnightInFrom
}

/**
 * Check if the time frame list cover whole day without overlaps.
 */
export const isWholeDayCovered = (timeFrameList: TimeFrameModel[]): boolean => {
  /* normalize time frames to minutes from midnight */
  const normalizedTimeFrameList = timeFrameList.map(frame => ({
    from: minutesFromMidnight(frame.from),
    to: minutesFromMidnight(frame.to),
  }))

  /* sort in ascending order by 'from' time -> '00:00' to '23:59' */
  normalizedTimeFrameList.sort((frame1, frame2) => frame1.from - frame2.from)

  /* check whole day covered without overlaps */
  let totalCoverage = 0
  let previousTo = 0
  for (const frame of normalizedTimeFrameList) {
    const { from, to } = frame

    /* check for overlap */
    if (from < previousTo) {
      return false
    }

    /* add to total coverage */
    totalCoverage += to - from
    previousTo = to
  }

  /* check total coverage is 1439 minutes (24h - 1 minute) */
  return totalCoverage === ONE_DAY_IN_MINUTES - 1
}

export const checkIsNotMoreThanTwoDecimalPoint = (value: number): boolean => {
  return /^\d+(\.\d{1,2})?$/.test(value.toString())
}

export const checkIsNotMoreThanThreeDecimalPoint = (value: number): boolean => {
  return /^\d+(\.\d{1,3})?$/.test(value.toString())
}

/**
 * Whether at least one weekday is selected in the given `Days` object.
 */
export const hasSelectedWeekday = (weekdays: Days): boolean => {
  return ALL_DAYS.some(day => weekdays[day])
}

/**
 * Check that a list of minute intervals tiles a full day (00:00 - 23:59) without gaps or overlaps.
 * Intervals are given as minutes from midnight (`00:00` -> 0, `23:59` -> 1439).
 */
export const doMinuteIntervalsCoverWholeDay = (intervals: { from: number; to: number }[]): boolean => {
  const sortedIntervals = [...intervals].sort((a, b) => a.from - b.from)

  let totalCoverage = 0
  let previousTo = 0
  for (const { from, to } of sortedIntervals) {
    /* overlap: a new interval starts before the previous one ended */
    if (from < previousTo) {
      return false
    }
    totalCoverage += to - from
    previousTo = to
  }

  /**
   * With overlaps rejected, a full coverage sum of 1439 minutes can only be reached when the
   * intervals are contiguous from 00:00 to 23:59, so gaps are implicitly rejected as well.
   */
  return totalCoverage === ONE_DAY_IN_MINUTES - 1
}

/**
 * Check that every weekday is fully covered by the given manual power price periods.
 * For each weekday, the periods that include it must tile the whole day without gaps or overlaps,
 * so there is exactly one price for every point in time on every day of the week.
 */
export const isEveryWeekdayCovered = (periods: { weekdays: Days; from: Date; to: Date }[]): boolean => {
  return ALL_DAYS.every((day: Day) => {
    const intervals = periods
      .filter(period => period.weekdays[day])
      .map(period => ({ from: minutesFromMidnight(period.from), to: minutesFromMidnight(period.to) }))

    if (intervals.length === 0) {
      return false
    }

    return doMinuteIntervalsCoverWholeDay(intervals)
  })
}

/**
 * Check whether any weekday has two periods whose times overlap, i.e. more than one price would
 * apply to the same point in time on the same weekday.
 */
export const hasOverlappingWeekdayPeriods = (periods: { weekdays: Days; from: Date; to: Date }[]): boolean => {
  return ALL_DAYS.some((day: Day) => {
    const intervals = periods
      .filter(period => period.weekdays[day])
      .map(period => ({ from: minutesFromMidnight(period.from), to: minutesFromMidnight(period.to) }))
      .sort((a, b) => a.from - b.from)

    let maxEnd = -Infinity
    for (const { from, to } of intervals) {
      /* a period starts before the furthest end seen so far -> it overlaps an earlier period */
      if (from < maxEnd) {
        return true
      }
      maxEnd = Math.max(maxEnd, to)
    }
    return false
  })
}

/**
 * Check whether the periods contain at least two different price values.
 *
 * The same price may be reused across any number of independent periods, but if every period
 * shares the same single price the manual tariff is effectively static and must be rejected.
 */
export const hasAtLeastTwoDistinctPrices = (periods: { price: number }[]): boolean => {
  return new Set(periods.map(period => period.price)).size >= 2
}
